import { Alert, AlertTitle, Box, Button, CircularProgress } from '@mui/material';
import { STATE_PUBLISHED } from '@opengov/stories-shared/constants';
import {
  IPublication,
  IPublicationBrandings,
  IPublicationFooterSettings,
  IPublicationNavMenuItem,
  IPublicationTocSettings
} from '@opengov/stories-shared/types';
import React, { useEffect, useState } from 'react';

import { onlinePublication as messages } from '../../../../locale/stories.en.locale.json';
import { IOnlinePublicationMessages, ISessionUser, IStoriesFolder } from '../../../../types';
import { ConfirmationModal } from '../../../common/components/ConfirmationModal';
import { NavigationBlockerPopup } from '../../Organize/NavigationBlockerPopup';

import { ITab, TabBar } from '../../CommonComponents/TabBar/TabBar';
import {
  FOOTER,
  FooterTab,
  MENU_STYLING,
  MenuStylingTab,
  NAVIGATION_MENU,
  TABLE_OF_CONTENTS,
  TABS_PUBLICATION,
  TableOfContentsTab
} from '../../CommonComponents/TabBar/TabBarUtils';

import { PublicationBrandingTab } from './Branding';
import {
  SetupOnlinePublicationUiProvider,
  useSetupOnlinePublicationUi
} from './context/SetupOnlinePublicationUi.context';
import { Footer } from './Footer';
import { Header } from './Header';
import { usePublicationById } from '../PublicationView/hooks/usePublicationById';
import {
  useSetTocAsLandingPage,
  useUpdatePublicationUpdatedAt
} from '../PublicationView/hooks/usePublicationMutations';
import { useDeactivateOnlinePublicationUrl } from './hooks/useDeactivateOnlinePublicationUrl';
import { useDeleteNavigationMenu } from './hooks/useDeleteNavigationMenu';
import { useInsertNavigationMenu } from './hooks/useInsertNavigationMenu';
import { useMenuStoriesForMenuId } from './hooks/useMenuStoriesForMenuId';
import { useNavigationMenus } from './hooks/useNavigationMenus';
import { useOrganizeMenuItems } from './hooks/useOrganizeMenuItems';
import { usePublicationBrandings } from './hooks/usePublicationBrandings';
import { usePublicationFooterSettings } from './hooks/usePublicationFooterSettings';
import { useSaveFooterSettings } from './hooks/useSaveFooterSettings';
import { useSavePublicationBrandings } from './hooks/useSavePublicationBrandings';
import { useSaveTocSettings } from './hooks/useSaveTocSettings';
import { useTocSettings } from './hooks/useTocSettings';
import { NavigationMenu } from './NavigationMenu/NavigationMenu';
import { styles } from './SetupOnlinePublication.styles';
import { TableOfContentsContainer } from './TableOfContents/TableOfContents';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';
import { useHasAdminPrivilegeForPublication } from '../../../hooks/subjectClaims';
import { alphabetizeNodes, sortItemsByOrder } from '../../../utils';

export type ISetupOnlinePublicationProps = {
  publicationId: string;
  entityId: number;
  entityUuid: string;
  entityName: string;
  entitySubdomain: string;
  isOpenGovAdmin: boolean;
  enableGoogleTranslateForPublications?: boolean;
  storiesAccessControlEnabled: boolean;
  messages: IOnlinePublicationMessages;
  user: ISessionUser;
  initialActiveTab: ITab | null;
  initialMenuId: string | null;
  navigateToPreviewOnlinePublication(entitySubdomain: string, publicationId: string): void;
  navigateToPublicationView(entitySubdomain: string, publicationId: string): unknown;
  navigateToOnlinePublicationMenu(entitySubdomain: string, publicationId: string, menuId: string): void;
  navigateToSetupOnlinePublicationTab(entitySubdomain: string, publicationId: string, tabId: string): void;
  openModal(modal: string): IAction<string, string>;
};

const SetupOnlinePublicationInternal: React.FC<ISetupOnlinePublicationProps> = (props) => {
  const {
    publicationId,
    entityId,
    entitySubdomain,
    entityUuid,
    user,
    storiesAccessControlEnabled,
    enableGoogleTranslateForPublications,
    initialActiveTab,
    initialMenuId,
    messages: routeMessages,
    navigateToPreviewOnlinePublication,
    navigateToPublicationView,
    navigateToOnlinePublicationMenu,
    navigateToSetupOnlinePublicationTab
  } = props;

  const isAdmin = useHasAdminPrivilegeForPublication(
    publicationId && user?.uuid && entityUuid
      ? {
        publicationId,
        subjectId: user.uuid,
        entityUuid,
        storiesAccessControlEnabled
      }
      : null
  );

  // Static toast/copy strings come from the locale JSON; format messages like
  // `publicationName({ name })` need the locale-bound function variant, which
  // lives on the prop wired by the route's `storiesMsgs(state)` selector.
  const formatMessages = routeMessages;

  // Publication is fetched via hook, not route-prefetched state.
  const publicationQuery = usePublicationById(
    publicationId && entityId > 0 ? { publicationId } : null,
    messages.somethingWentWrongError
  );
  const publication = publicationQuery.data ?? null;
  const isStoreHydrated = !!publicationId && entityId > 0;
  const navigationMenusParams = isStoreHydrated ? { publicationId } : null;
  const brandingsParams = isStoreHydrated ? { publicationId } : null;
  const tocParams = isStoreHydrated ? { publicationId } : null;
  const footerParams = isStoreHydrated ? { entityId, publicationId } : null;

  const navigationMenusQuery = useNavigationMenus(navigationMenusParams, messages.failedMenuItems);
  const brandingsQuery = usePublicationBrandings(brandingsParams, messages.failedToGetBranding);
  const tocSettingsQuery = useTocSettings(tocParams, messages.tableOfContentsTab.failedToFetchToc);
  const footerSettingsQuery = usePublicationFooterSettings(footerParams, messages.failureText);

  const navigationMenuItems = navigationMenusQuery.data ?? [];
  const publicationBrandings = brandingsQuery.data ?? null;
  const tocSettings = tocSettingsQuery.data ?? null;
  const publicationFooterSettings = footerSettingsQuery.data ?? null;

  const sortedMenuItems = React.useMemo<IStoriesFolder[]>(
    () =>
      sortItemsByOrder(
        alphabetizeNodes(
          navigationMenuItems.map((menu) => ({
            id: menu.id,
            displayName: menu.title,
            toggled: false,
            order: menu.order
          }))
        )
      ),
    [navigationMenuItems]
  );

  const isPreviewAvailable = navigationMenuItems.some(
    (menu) => !!menu.publicationNavigationMenuStories?.length
  );

  const saveBrandingsMutation = useSavePublicationBrandings(
    { publicationId: publicationId ?? '' },
    {
      logoUploadedTitle: messages.logoUploaded,
      logoUploadedBody: messages.logoUploadedDesc,
      logoRemovedTitle: messages.logoRemoved,
      logoRemovedBody: messages.logoRemovedDesc,
      errorTitle: messages.somethingWentWrongError,
      errorBody: messages.failedToSaveBranding
    }
  );
  const saveTocMutation = useSaveTocSettings(
    { publicationId: publicationId ?? '' },
    { errorTitle: messages.somethingWentWrongError, errorBody: messages.changesSavedFailed }
  );
  const saveFooterMutation = useSaveFooterSettings(
    { entityId, publicationId: publicationId ?? '' },
    { errorTitle: messages.failureText, errorBody: messages.failedToSavePfs }
  );
  const insertMenu = useInsertNavigationMenu({
    successTitle: messages.newMenuAdded,
    successBody: ({ name }) => messages.newMenuAddedDescription.replace('{name}', name),
    errorTitle: messages.somethingWentWrongError
  });
  const deleteMenuMutation = useDeleteNavigationMenu({
    successTitle: messages.menuDeleted,
    successBody: ({ name }) => messages.menuDeletedDesc.replace('{name}', name),
    errorTitle: messages.somethingWentWrongError,
    errorBody: messages.failedToDeleteMenu
  });
  const organizeMenusMutation = useOrganizeMenuItems({
    successTitle: messages.changesSaved,
    successBody: messages.changesSavedSuccessfully,
    errorTitle: messages.somethingWentWrongError,
    errorBody: messages.changesSavedFailed
  });
  const deactivateMutation = useDeactivateOnlinePublicationUrl({ entitySubdomain, entityId });

  // Publication is fetched via TanStack Query (no route onEnter prefetch). Until
  // data resolves, `publication` is null — do not mount content that reads
  // `publication.status` / title or PageErrorBoundary will catch a TypeError
  // and pin the page on the full-page error fallback.
  if (!publication) {
    return (
      <Box sx={styles.loading} data-test="setup-online-publication-loading">
        <CircularProgress role="status" aria-label="Loading" />
      </Box>
    );
  }

  return (
    <SetupOnlinePublicationUiProvider
      initialState={{
        activeTab: initialActiveTab,
        selectedMenuId: initialMenuId
      }}
    >
      <SetupOnlinePublicationContent
        publicationId={publicationId}
        entityId={entityId}
        entitySubdomain={entitySubdomain}
        publication={publication}
        user={user}
        isAdmin={isAdmin}
        enableGoogleTranslateForPublications={!!enableGoogleTranslateForPublications}
        formatMessages={formatMessages}
        urlActiveTab={initialActiveTab}
        urlMenuId={initialMenuId}
        navigationMenuItems={navigationMenuItems}
        sortedMenuItems={sortedMenuItems}
        publicationBrandings={publicationBrandings}
        tocSettings={tocSettings}
        publicationFooterSettings={publicationFooterSettings}
        isPreviewAvailable={isPreviewAvailable}
        // `isFetching` (not `isLoading`) so disabled queries — those gated on
        // `isStoreHydrated` — don't report a perpetual loading state.
        loadingMenuItems={navigationMenusQuery.isFetching}
        loadingPublicationBrandings={brandingsQuery.isFetching || saveBrandingsMutation.isLoading}
        loadingTocSettings={tocSettingsQuery.isFetching || saveTocMutation.isLoading}
        deactivateMutation={deactivateMutation}
        saveBrandings={async (payload: Partial<IPublicationBrandings>) => {
          if (!publicationBrandings) return;
          // The codegen `_set_input` shape aligns with
          // `Partial<IPublicationBrandings>` at runtime — type-level
          // differences are paper-only (e.g. `Maybe<T>` vs optional `T`).
          await saveBrandingsMutation.mutateAsync({
            id: publicationBrandings.id,
            set: payload as never
          });
        }}
        saveTocSettings={async (payload: Partial<IPublicationTocSettings>) => {
          if (!tocSettings) return;
          await saveTocMutation.mutateAsync({ id: tocSettings.id, set: payload as never });
        }}
        saveFooterSettings={async (payload: Partial<IPublicationFooterSettings>) => {
          if (!publicationFooterSettings) return;
          await saveFooterMutation.mutateAsync({
            id: publicationFooterSettings.id,
            set: payload as never
          });
        }}
        insertPublicationNavigationMenu={(variables) => insertMenu.insertNavigationMenu(variables)}
        deleteNavigationMenuById={async ({ id, title, publicationId }) => {
          await deleteMenuMutation.deleteNavigationMenu({ id, title, publicationId });
        }}
        organizeMenuItemsByOrder={async (variables) => {
          await organizeMenusMutation.mutateAsync(variables);
        }}
        navigateToPreviewOnlinePublication={navigateToPreviewOnlinePublication}
        navigateToPublicationView={navigateToPublicationView}
        navigateToOnlinePublicationMenu={navigateToOnlinePublicationMenu}
        navigateToSetupOnlinePublicationTab={navigateToSetupOnlinePublicationTab}
      />
    </SetupOnlinePublicationUiProvider>
  );
};

type ContentProps = {
  publicationId: string | undefined;
  entityId: number;
  entitySubdomain: string;
  publication: IPublication;
  user: ISessionUser;
  isAdmin: boolean;
  enableGoogleTranslateForPublications: boolean;
  formatMessages: IOnlinePublicationMessages;
  // Latest values derived from the URL by the route selector. Synced into
  // the UI context via a useEffect so router navigations (tab clicks, browser
  // back/forward, deep links) update the active tab / selected menu without
  // a full page remount.
  urlActiveTab: ITab | null;
  urlMenuId: string | null;
  navigationMenuItems: IPublicationNavMenuItem[];
  sortedMenuItems: IStoriesFolder[];
  publicationBrandings: IPublicationBrandings | null;
  tocSettings: IPublicationTocSettings | null;
  publicationFooterSettings: IPublicationFooterSettings | null;
  isPreviewAvailable: boolean;
  loadingMenuItems: boolean;
  loadingPublicationBrandings: boolean;
  loadingTocSettings: boolean;
  deactivateMutation: ReturnType<typeof useDeactivateOnlinePublicationUrl>;
  saveBrandings(payload: Partial<IPublicationBrandings>): Promise<void>;
  saveTocSettings(payload: Partial<IPublicationTocSettings>): Promise<void>;
  saveFooterSettings(payload: Partial<IPublicationFooterSettings>): Promise<void>;
  insertPublicationNavigationMenu(props: {
    publicationId: string;
    title: string;
    userId: number;
    order: number;
  }): Promise<{ menuId: string; publicationId: string } | null>;
  deleteNavigationMenuById(props: { id: string; title: string; publicationId: string }): Promise<void>;
  organizeMenuItemsByOrder(props: { orderedMenuIds: string[]; publicationId: string }): Promise<void>;
  navigateToPreviewOnlinePublication(entitySubdomain: string, publicationId: string): void;
  navigateToPublicationView(entitySubdomain: string, publicationId: string): unknown;
  navigateToOnlinePublicationMenu(entitySubdomain: string, publicationId: string, menuId: string): void;
  navigateToSetupOnlinePublicationTab(entitySubdomain: string, publicationId: string, tabId: string): void;
};

function SetupOnlinePublicationContent(props: ContentProps) {
  const {
    publicationId,
    entityId,
    entitySubdomain,
    publication,
    user,
    isAdmin,
    enableGoogleTranslateForPublications,
    formatMessages,
    urlActiveTab,
    urlMenuId,
    navigationMenuItems,
    sortedMenuItems,
    publicationBrandings,
    tocSettings,
    publicationFooterSettings,
    isPreviewAvailable,
    loadingMenuItems,
    loadingPublicationBrandings,
    loadingTocSettings,
    deactivateMutation,
    saveBrandings,
    saveTocSettings,
    saveFooterSettings,
    insertPublicationNavigationMenu,
    deleteNavigationMenuById,
    organizeMenuItemsByOrder,
    navigateToPreviewOnlinePublication,
    navigateToPublicationView,
    navigateToOnlinePublicationMenu,
    navigateToSetupOnlinePublicationTab
  } = props;

  const { state: uiState, dispatch: uiDispatch } = useSetupOnlinePublicationUi();
  const { selectedMenuId, isOrganizationMenuOrStoriesInProgress } = uiState;

  // Replaces the retired `publicationViewEffects.setTocAsLandingPage`
  // (was injected as the `setTocAsLandingPageOnPublication` route prop).
  const { mutateAsync: doSetTocAsLandingPage } = useSetTocAsLandingPage({
    title: formatMessages.somethingWentWrongError,
    body: formatMessages.failedToUpdate
  });
  const { mutateAsync: doUpdatePublicationUpdatedAt } = useUpdatePublicationUpdatedAt();

  // Derive the active tab directly from the URL-driven prop rather than
  // storing it in context state. The context copy was previously synced via a
  // useEffect, but that sync could race with Redux store updates and transiently
  // reset the tab to Navigation Menu even when the URL was correct.
  const activeTab = urlActiveTab ?? TABS_PUBLICATION[0];

  useEffect(() => {
    if (urlMenuId !== selectedMenuId) {
      uiDispatch({ type: 'SET_SELECTED_MENU_ID', menuId: urlMenuId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMenuId]);

  const menuStoriesQuery = useMenuStoriesForMenuId(
    selectedMenuId ? { menuId: selectedMenuId } : null,
    messages.failedToUpdate
  );
  const menuStories = menuStoriesQuery.data ?? [];
  // `isFetching` (not `isLoading`) so that an unselected menu — which leaves
  // the query disabled — doesn't render a perpetual spinner.
  const fetchingMenuStories = menuStoriesQuery.isFetching;

  const selectedMenuName = React.useMemo(() => {
    if (!selectedMenuId) return '';
    const match = sortedMenuItems.find((item) => item.id === selectedMenuId);
    return match?.displayName ?? '';
  }, [selectedMenuId, sortedMenuItems]);

  const [showBlockerPopup, setShowBlockerPopup] = useState(false);
  const [showDeactivateLinkModal, setShowDeactivateLinkModal] = useState<boolean>(false);
  const [navLink, setNavLink] = useState<string | null>(null);

  const handleBrowserNavigation = (event: BeforeUnloadEvent) => {
    if (!navLink && !showBlockerPopup && isOrganizationMenuOrStoriesInProgress) {
      event.preventDefault();
      event.returnValue = messages.unsavedChangesDescription;
      return '';
    }
  };

  const handleNavigationClick = (event: Event) => {
    if (isOrganizationMenuOrStoriesInProgress) {
      const target = event.target as HTMLElement;
      let targetLink: string | undefined;
      if (target.tagName === 'A') {
        targetLink = (target as HTMLAnchorElement).href;
      } else {
        targetLink = target.closest('a')?.href;
      }
      event.preventDefault();
      setNavLink(targetLink ?? null);
      setShowBlockerPopup(true);
    }
  };

  const handleBackClick = () => {
    uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: false });
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleBrowserNavigation);
    window.document.querySelector('nav')?.addEventListener('click', handleNavigationClick);
    window.addEventListener('popstate', handleBackClick);

    return () => {
      window.removeEventListener('popstate', handleBackClick);
      window.removeEventListener('beforeunload', handleBrowserNavigation);
      window.document.querySelector('nav')?.removeEventListener('click', handleNavigationClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBlockerPopup, isOrganizationMenuOrStoriesInProgress]);

  // Do not gate on `updatePublicationPreview`. That flag only means "Header
  // should regenerate the preview URL" — it is set as soon as a save succeeds
  // and stays true until the regenerate finishes. Blocking tab clicks on it
  // (a merge mix-up with the retired compose-app `updatingPreview` loading
  // flag) left Menu Styling unreachable right after Add Stories / Add Menu.
  const navigateToTab = (tab: ITab) => {
    if (isOrganizationMenuOrStoriesInProgress) {
      setNavLink(tab.path);
      setShowBlockerPopup(true);
      return;
    }
    navigateToSetupOnlinePublicationTab(entitySubdomain, publicationId ?? '', tab.path);
  };

  // After any save resolves, flip the preview-refresh flag so the Header
  // regenerates the share/preview URL — matches the legacy flow where every
  // save effect dispatched `setUpdatePublicationPreview(true)`.
  const triggerPreviewRefresh = React.useCallback(() => {
    uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
  }, [uiDispatch]);

  // The save hooks already surface their own error toast via `useToast`; the
  // `.catch` here just swallows the rejection so it doesn't bubble up as an
  // unhandled promise. On a failed save we intentionally skip the refresh.
  const handleUpdateBrandings = (payload: Partial<IPublicationBrandings>) => {
    void saveBrandings(payload).then(triggerPreviewRefresh).catch(() => undefined);
  };
  const handleUpdateTocSettings = (payload: Partial<IPublicationTocSettings>) => {
    void saveTocSettings(payload).then(triggerPreviewRefresh).catch(() => undefined);
  };
  const handleUpdateFooterSettings = (payload: Partial<IPublicationFooterSettings>) => {
    void saveFooterSettings(payload).then(triggerPreviewRefresh).catch(() => undefined);
  };

  // Jump to the newly created menu.
  const handleInsertNavigationMenu = async (variables: {
    publicationId: string;
    title: string;
    userId: number;
    order: number;
  }) => {
    const result = await insertPublicationNavigationMenu(variables);
    if (result) {
      uiDispatch({ type: 'SET_SELECTED_MENU_ID', menuId: result.menuId });
      navigateToOnlinePublicationMenu(entitySubdomain, result.publicationId, result.menuId);
    }
    return result;
  };

  // Auto-sync TOC visibility with preview availability until user toggles TOC.
  useEffect(() => {
    if (!tocSettings) return;
    const { hasUserToggledToc, showTableOfContents } = tocSettings;
    if (hasUserToggledToc) return;
    if (!showTableOfContents && isPreviewAvailable) {
      handleUpdateTocSettings({ showTableOfContents: true });
    } else if (showTableOfContents && !isPreviewAvailable) {
      handleUpdateTocSettings({ showTableOfContents: false });
    }
    // Keep dependencies data-only to avoid effect churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewAvailable, tocSettings]);

  const renderPublicationBranding = () => (
    <PublicationBrandingTab
      entitySubdomain={entitySubdomain}
      publicationBrandings={publicationBrandings}
      messages={formatMessages}
      handleUpdateBrandings={handleUpdateBrandings}
    />
  );

  const renderTableOfContents = () => {
    return (
      <TableOfContentsContainer
        entitySubdomain={entitySubdomain}
        tocSettings={tocSettings}
        publication={publication}
        publicationBrandings={publicationBrandings}
        publicationNavigationMenuItems={navigationMenuItems}
        setTocAsLandingPageOnPublication={({ setTocAsLandingPage }) => {
          if (!publication) return;
          void doSetTocAsLandingPage({ publicationId: publication.id, setTocAsLandingPage })
            .then(() => doUpdatePublicationUpdatedAt({ publicationId: publication.id, userId: user.id }))
            .then(() => uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true }));
        }}
        handleUpdateTocSettings={handleUpdateTocSettings}
      />
    );
  };

  const navigateToNewFolder = () => {
    if (navLink) {
      const link = navLink;
      setNavLink(null);
      if ([MenuStylingTab.path, TableOfContentsTab.path, FooterTab.path].includes(link)) {
        uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: false });
        navigateToSetupOnlinePublicationTab(entitySubdomain, publicationId ?? '', link);
      } else {
        window.location.href = link;
      }
    }
    setShowBlockerPopup(false);
  };

  const setPreviousSelectedFolder = () => {
    setNavLink(null);
    setShowBlockerPopup(false);
  };

  const renderDeactivateLinkConfirmationModal = () => {
    const handleClose = () => setShowDeactivateLinkModal(false);
    const handleConfirm = () => {
      void deactivateMutation
        .mutateAsync({ publication, userId: user.id })
        .then(handleClose)
        .catch(handleClose);
    };

    return (
      <ConfirmationModal
        title={messages.deactivateLink}
        description={messages.deactivateLinkDesc}
        dataTest="deactivate-link-confirmation-modal"
        confirmationText={messages.deactivate}
        actionPending={deactivateMutation.isLoading}
        onCancel={handleClose}
        onProceed={handleConfirm}
      />
    );
  };

  const renderModals = () => {
    const navigationBlockerModal = showBlockerPopup && (
      <NavigationBlockerPopup
        messages={messages as unknown as Parameters<typeof NavigationBlockerPopup>[0]['messages']}
        onConfirmNavigate={navigateToNewFolder}
        onCancel={setPreviousSelectedFolder}
      />
    );
    const renderDeactivateLinkModal = showDeactivateLinkModal && renderDeactivateLinkConfirmationModal();
    return [navigationBlockerModal, renderDeactivateLinkModal];
  };

  const renderNavigationMenu = () => {
    return (
      <NavigationMenu
        entityId={entityId}
        entitySubdomain={entitySubdomain}
        messages={formatMessages}
        loadingMenuItems={loadingMenuItems}
        menuStories={menuStories}
        fetchingMenuStories={fetchingMenuStories}
        menuItems={sortedMenuItems}
        publicationId={publicationId ?? ''}
        userId={user?.id}
        insertPublicationNavigationMenu={handleInsertNavigationMenu}
        organizeMenuItemsByOrder={organizeMenuItemsByOrder}
        publicationBrandings={publicationBrandings}
        handleUpdateBrandings={handleUpdateBrandings}
        navigateToOnlinePublicationMenu={navigateToOnlinePublicationMenu}
        selectedMenuName={selectedMenuName}
        deleteNavigationMenuById={deleteNavigationMenuById}
        enableGoogleTranslateForPublications={enableGoogleTranslateForPublications}
      />
    );
  };

  const renderFooterContent = () => {
    // Footer was historically rendered behind a null-guard at the parent layer;
    // matching that here keeps the existing strict-non-null prop type unchanged.
    if (!publicationFooterSettings) return null;
    return (
      <Footer
        publicationFooterSettings={publicationFooterSettings}
        messages={formatMessages}
        handleUpdateFooterSettings={handleUpdateFooterSettings}
      />
    );
  };

  const renderContent = () => {
    switch (activeTab?.name) {
      case NAVIGATION_MENU:
        return renderNavigationMenu();
      case MENU_STYLING:
        return renderPublicationBranding();
      case TABLE_OF_CONTENTS:
        return renderTableOfContents();
      case FOOTER:
        return renderFooterContent();

      default:
        return <div>{activeTab?.name}</div>;
    }
  };

  const renderDeactivateOnlinePublicationLinkBanner = () => {
    if (!publication) return null;
    const onlinePublicationDetails = (
      <li key="link-message-3">
        {messages.deactivateBannerPointThree}
        <Box
          component="a"
          href={publication.publicUrl ?? ""}
          sx={styles.deactivateAnchorUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {publication.publicUrl}
        </Box>
      </li>
    );
    return (
      <Alert
        data-test="deactivate-link-banner"
        severity="info"
        sx={styles.deactivateBanner}
      >
        <AlertTitle>{messages.deactivateBannerHeader}</AlertTitle>
        <Box sx={styles.bannerDetailContent}>
          <ul>
            {publication.publicUrl !== null && onlinePublicationDetails}
            <li key="link-message-1">{messages.deactivateBannerPointOne}</li>
            <li key="link-message-2">{messages.deactivateBannerPointTwo}</li>
          </ul>
          <Button
            data-test="deactivate-link-button"
            sx={styles.deactivateButton}
            variant="text"
            size="small"
            onClick={() => setShowDeactivateLinkModal(true)}
          >
            {messages.deactivateLink}
          </Button>
        </Box>
      </Alert>
    );
  };

  return (
    <Box sx={styles.container}>
      {renderModals()}
      <Header
        isPreviewAvailable={isPreviewAvailable}
        loadingPublicationBrandings={loadingPublicationBrandings}
        loadingTocSettings={loadingTocSettings}
        publicationId={publicationId ?? ''}
        publication={publication}
        user={user}
        entitySubdomain={entitySubdomain}
        messages={formatMessages}
        navigateToPreviewOnlinePublication={navigateToPreviewOnlinePublication}
        navigateToPublicationView={navigateToPublicationView}
        isAdmin={isAdmin}
        publicationNavigationMenuItems={navigationMenuItems}
        enableGoogleTranslateForPublications={enableGoogleTranslateForPublications}
      />
      {publication?.status === STATE_PUBLISHED && renderDeactivateOnlinePublicationLinkBanner()}
      <Box sx={styles.tabBarContent}>
        <TabBar activeTab={activeTab} navigateToTab={navigateToTab} tabs={TABS_PUBLICATION} />
      </Box>
      <Box sx={styles.content}>{renderContent()}</Box>
    </Box>
  );
}

export const SetupOnlinePublication: React.FC<ISetupOnlinePublicationProps> = (props) => {
  return (
    <PageErrorBoundary viewName={ViewName.SetupOnlinePublicationPage}>
      <SetupOnlinePublicationInternal {...props} />
    </PageErrorBoundary>
  );
};
